<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<crucible-dialog
  [dialogTitle]="data.scoringCategory.id ? 'Edit Scoring Category' : 'Add Scoring Category'"
  [form]="form"
  submitLabel="Save"
  [submitDisabled]="!form.valid || !form.dirty || !data.canEdit"
  [guardUnsavedWork]="true"
  (cancel)="handleEditComplete(false)"
  (submit)="handleEditComplete(true)"
>
@if (data && data.scoringCategory) {
  <div crucibleDialogContent [formGroup]="form">
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Scoring Category Description</mat-label>
          <textarea matInput formControlName="description"></textarea>
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="A name or description for this scoring category as it appears on the scoresheet."
          #descTip="matTooltip" (click)="descTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field">
          <mat-label>Display Order</mat-label>
          <input matInput formControlName="displayOrder" />
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Numeric order in which this category appears on the scoresheet. Lower numbers appear first."
          #orderTip="matTooltip" (click)="orderTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      @if (data.displayScoringModelbyMoveNumber) {
        <div class="field-row">
          <mat-form-field class="description-field">
            <mat-label>First Move to Display</mat-label>
            <input matInput formControlName="moveNumberFirstDisplay" />
          </mat-form-field>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="The earliest evaluation move in which this category is visible to participants."
            #firstMoveTip="matTooltip" (click)="firstMoveTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      }
    </div>
    <div class="add-margin">
      @if (data.displayScoringModelbyMoveNumber) {
        <div class="field-row">
          <mat-form-field class="description-field">
            <mat-label>Last Move to Display</mat-label>
            <input matInput formControlName="moveNumberLastDisplay" />
          </mat-form-field>
          <button mat-icon-button type="button" class="info-button" tabindex="-1"
            matTooltip="The latest evaluation move in which this category is visible to participants."
            #lastMoveTip="matTooltip" (click)="lastMoveTip.toggle()">
            <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
          </button>
        </div>
      }
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Calculation Equation</mat-label>
          <textarea matInput formControlName="calculationEquation"></textarea>
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Formula that computes this category's score from the selected options. Supports the following placeholders: {count} (number of selected options), {min} (smallest selected value), {max} (largest selected value), {sum} (total of selected values), and {modifier} (value of the selected modifier option, or 1.0 if no modifier is selected and none is required, or 0.0 if a modifier is required but none is selected)."
          #eqTip="matTooltip" (click)="eqTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field">
          <mat-label>Calculation Weight</mat-label>
          <input matInput formControlName="scoringWeight" />
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Multiplier applied to this category's score when calculating the scoring model's overall score. 1.0 is neutral; higher values make this category count more."
          #weightTip="matTooltip" (click)="weightTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-form-field class="description-field full-width">
          <mat-label>Scoring Option Selection Type</mat-label>
          <mat-select placeholder="Select a Scoring Option Selection Type" formControlName="scoringOptionSelection">
            @for (item of data.scoringOptionSelections; track item) {
              <mat-option [value]="item">
                {{ item }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="Controls how participants select options within this category: Single lets the participant pick one option, Multiple lets them pick several, and None disables selection."
          #selTypeTip="matTooltip" (click)="selTypeTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
    <div class="add-margin">
      <div class="field-row">
        <mat-checkbox formControlName="isModifierRequired">Modifier Selection Required</mat-checkbox>
        <button mat-icon-button type="button" class="info-button" tabindex="-1"
          matTooltip="When enabled, participants must select one of the modifier options in this category before their response is considered complete. Modifiers are selected separately and can adjust the category's score via the calculation equation."
          #modReqTip="matTooltip" (click)="modReqTip.toggle()">
          <mat-icon class="mdi-20px" fontIcon="mdi-information-outline"></mat-icon>
        </button>
      </div>
    </div>
  </div>
}
</crucible-dialog>
